/**
 * useInfiniteScroll Hook
 *
 * Reusable infinite scroll hook using IntersectionObserver.
 * Returns a ref to attach to a sentinel element at the bottom of a list.
 * When the sentinel becomes visible, triggers loadMore.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

interface UseInfiniteScrollOptions {
  /** Callback to load more items */
  onLoadMore: () => void;
  /** Whether there are more items to load */
  hasMore: boolean;
  /** Whether currently loading */
  loading: boolean;
  /** Root margin for IntersectionObserver (default: '100px' for early trigger) */
  rootMargin?: string;
  /** Threshold for IntersectionObserver (default: 0) */
  threshold?: number;
}

interface UseInfiniteScrollResult {
  /** Ref to attach to sentinel element */
  sentinelRef: (node?: Element | null) => void;
  /** Whether the sentinel is in view */
  inView: boolean;
}

/**
 * Hook for implementing infinite scroll behavior
 *
 * @example
 * ```tsx
 * const { sentinelRef } = useInfiniteScroll({
 *   onLoadMore: loadMore,
 *   hasMore,
 *   loading,
 * });
 *
 * return (
 *   <div>
 *     {items.map(item => <Item key={item.id} {...item} />)}
 *     <div ref={sentinelRef} />
 *   </div>
 * );
 * ```
 */
export function useInfiniteScroll({
  onLoadMore,
  hasMore,
  loading,
  rootMargin = '100px',
  threshold = 0,
}: UseInfiniteScrollOptions): UseInfiniteScrollResult {
  const { ref, inView } = useInView({
    rootMargin,
    threshold,
  });

  // Track if we're currently loading to prevent duplicate requests
  // This ref prevents race conditions when loading state changes rapidly
  const isLoadingRef = useRef(false);

  // Wrap onLoadMore to handle async completion and reset loading state
  const wrappedLoadMore = useCallback(async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    try {
      await Promise.resolve(onLoadMore());
    } finally {
      // Reset after the load completes (whether sync or async)
      isLoadingRef.current = false;
    }
  }, [onLoadMore]);

  const handleLoadMore = useCallback(() => {
    if (inView && hasMore && !loading && !isLoadingRef.current) {
      wrappedLoadMore();
    }
  }, [inView, hasMore, loading, wrappedLoadMore]);

  // Trigger load when conditions are met
  // Uses a small delay to debounce rapid state changes and prevent infinite loops
  useEffect(() => {
    if (!inView || !hasMore || loading) return;

    const timeoutId = setTimeout(() => {
      handleLoadMore();
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [inView, hasMore, loading, handleLoadMore]);

  return {
    sentinelRef: ref,
    inView,
  };
}
